// Pure 2D geometry helpers. No engine/render deps so they are trivially unit-testable.

export interface Vec2 {
  x: number;
  y: number;
}
export type Polygon = Vec2[];

export const v = (x: number, y: number): Vec2 => ({ x, y });

export function pointInPolygon(p: Vec2, poly: Polygon): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i]!;
    const b = poly[j]!;
    const intersect =
      a.y > p.y !== b.y > p.y &&
      p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x;
    if (intersect) inside = !inside;
  }
  return inside;
}

export function pointInAny(p: Vec2, polys: Polygon[]): boolean {
  return polys.some((poly) => pointInPolygon(p, poly));
}

/** Closest point to p on segment a-b. */
export function closestOnSegment(p: Vec2, a: Vec2, b: Vec2): Vec2 {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const len2 = abx * abx + aby * aby;
  if (len2 === 0) return { x: a.x, y: a.y };
  let t = ((p.x - a.x) * abx + (p.y - a.y) * aby) / len2;
  t = Math.max(0, Math.min(1, t));
  return { x: a.x + t * abx, y: a.y + t * aby };
}

/** Nearest point lying on the boundary of any polygon (for clamping off-mesh clicks). */
export function nearestOnPolys(p: Vec2, polys: Polygon[]): Vec2 {
  let best: Vec2 = polys[0]?.[0] ?? { x: p.x, y: p.y };
  let bestD = Infinity;
  for (const poly of polys) {
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const c = closestOnSegment(p, poly[i]!, poly[j]!);
      const d = dist2(p, c);
      if (d < bestD) {
        bestD = d;
        best = c;
      }
    }
  }
  return best;
}

export function dist2(a: Vec2, b: Vec2): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}
export function dist(a: Vec2, b: Vec2): number {
  return Math.sqrt(dist2(a, b));
}
